import React, { useEffect, useState } from "react";
import {
  Input,
  Label,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postSchema } from "@helpers/validation-schema";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import { resetError, resetState } from "../controller/post-slice";
import {
  addPost,
  editPost,
  getPost,
  getPostCount,
} from "../services/post-services";
import { postTypes } from "@constants/constants";
import Select from "react-select";
import { getAuthorList } from "../../author/services/author-services";
import { Client } from "@helpers/helpers";
import { API_METHOD } from "@constants/constants";
import { url } from "@constants/url-constants";
import FormRepeater from "../../../components/form-repeater/form-repeater";
import ViewModal from "@components/custom-table/ViewModel";
import { postUpdateSchema } from "@helpers/validation-schema";
import CustomLabel from "../../../components/CustomLabel";

export const AddPost = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const postId = pathname.split("/")[3];
  const success = useSelector((state) => state.post.success);
  const post = useSelector((state) => state.post.posts);
  const loading = useSelector((state) => state.post.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.post.successMessage);
  const errorMessage = useSelector((state) => state.post.errorMessage);
  const authors = useSelector((state) => state.author.authors);
  const options = {
    postOptions: postTypes?.map((row) => ({ value: row.id, label: row.name })),
    authOptions: [...(authors || [])]
      ?.sort((a) => (a.deleted_at === null ? -1 : 1))
      ?.map((row) => ({
        value: row._id,
        label: row.name,
        isDisabled: row.deleted_at !== null,
      })),
  };
  const [viewModel, setViewModel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileMeta, setFileMeta] = useState({
    size: 0,
    mime_type: "",
    width: 0,
    height: 0,
  });
  const [formRowsMeta, setFormRowsMeta] = useState({});

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(route === "edit" ? postUpdateSchema : postSchema),
    mode: "onBlur",
  });

  useEffect(() => {
    dispatch(getAuthorList({ pageCount: 1, limit: 10 }));
  }, []);

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getPostCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [setValue]);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getPost(postId));
    }
  }, [postId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/posts?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);
      setIsLoading(false);
      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const messages = item[fieldName];
        messages.forEach((message) => {
          toast.error(message);
          setError(fieldName, { type: "manual", message });
        });
        // setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (post) {
      setValue("title", post?.title);
      setValue("sorting", post?.sorting);
      setValue("type", post?.type);
      // const validAuthors = post?.original_author?.filter(
      //   (o) => o.deleted_at === null
      // );

      // setValue("original_author_ids", validAuthors?.map((o) => o._id) || []);
      setValue("original_author_ids", post?.original_author_ids);
      setValue("items", post?.contents);
      setValue("thumbnail", post?.thumbnail);
    }
  }, [post, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    console.log(data, "DATA");
    if (data?.type === "2" && data.original_author_ids && data.original_author_ids.length === 0 || (data?.type === "2" && data.original_author_ids === undefined)) {

      setError("original_author_ids", {
        type: "manual",
        message: "Author is required",
      });
      setIsLoading(false);

      return
    }
    try {
      let tempImageUrl = "";

      if (data.image) {
        const generatePresignedUrl = await handleImageUpload(data.image);
        if (generatePresignedUrl.imageTemp) {
          tempImageUrl = generatePresignedUrl.imageTemp;
        }
      }
      data.formRows.forEach((formRow) => {
        if (formRow.sorting) {
          formRow.sorting = parseInt(formRow.sorting, 10);
        }
      });
      // Process formRows sequentially
      // Process formRows sequentially

      const updatedFormRows = [];

      for (let i = 0; i < data.formRows.length; i++) {
        const formRow = data.formRows[i];

        if (formRow.image === null || formRow.type === 2) {
          updatedFormRows.push({
            ...formRow,
            image: {
              image_url: "",
              size: 0,
              mime_type: "",
              width: 0,
              height: 0,
            },
          });
        } else if (formRow.image && formRow.image instanceof File) {
          // Handle new file uploads
          const file = formRow.image;
          const img = new Image();

          const metadataPromise = new Promise((resolve) => {
            img.onload = () => {
              const metadata = {
                size: file.size / 1024, // size in KB
                mime_type: file.type,
                width: img.width,
                height: img.height,
              };
              resolve(metadata);
            };
            img.src = URL.createObjectURL(file);
          });

          try {
            const metadata = await metadataPromise;

            // Upload the image and get the URL
            const generatePresignedUrl = await handleImageUpload(file);
            if (generatePresignedUrl.imageTemp) {
              updatedFormRows.push({
                ...formRow,
                image: {
                  image_url: generatePresignedUrl.imageTemp,
                  size: metadata.size,
                  mime_type: metadata.mime_type,
                  width: metadata.width,
                  height: metadata.height,
                },
              });
            } else {
              toast.error(`Failed to upload image for form row ${i}`);
              updatedFormRows.push(formRow); // Push original if upload fails
            }
          } catch (error) {
            toast.error(
              `Error processing image for form row ${i}: ${error.message}`
            );
            updatedFormRows.push(formRow); // Push original if an error occurs
          }
        } else {
          // If image is neither null nor a File, push the formRow unchanged
          updatedFormRows.push(formRow);
        }
      }

      //  const updatedFormRows = [];
      //  for (let i = 0; i < data.formRows.length; i++) {
      //    const formRow = data.formRows[i];

      //    if (formRow.image && formRow.image instanceof File) {
      //      // Extract metadata for the image
      //      const file = formRow.image;
      //      const img = new Image();

      //      const metadataPromise = new Promise((resolve) => {
      //        img.onload = () => {
      //          const metadata = {
      //            size: file.size / 1024, // size in KB
      //            mime_type: file.type,
      //            width: img.width,
      //            height: img.height,
      //          };
      //          resolve(metadata);
      //        };
      //        img.src = URL.createObjectURL(file);
      //      });

      //      const metadata = await metadataPromise;

      //      // Handle new file uploads
      //      const generatePresignedUrl = await handleImageUpload(file);
      //      if (generatePresignedUrl.imageTemp) {
      //        updatedFormRows.push({
      //          ...formRow,
      //          image: {
      //            image_url: generatePresignedUrl.imageTemp,
      //            size: metadata.size,
      //            mime_type: metadata.mime_type,
      //            width: metadata.width,
      //            height: metadata.height,
      //          },
      //        });
      //      } else {
      //        toast.error(`Failed to upload image for form row ${i}`);
      //        updatedFormRows.push(formRow); // Push original if upload fails
      //      }
      //    } else {
      //      // If no image or not a File, push the formRow unchanged
      //      updatedFormRows.push(formRow);
      //    }
      //  }
      // for (let i = 0; i < data.formRows.length; i++) {
      //   const formRow = data.formRows[i];

      //   if (formRow.image) {
      //     if (formRow.image.image_url instanceof File) {
      //       const generatePresignedUrl = await handleImageUpload(
      //         formRow.image.image_url
      //       );

      //       // Update image_url in the formRows array at the same index
      //       if (generatePresignedUrl.imageTemp) {
      //         formRow.image.image_url = generatePresignedUrl.imageTemp;
      //         formRow.description = "";
      //       } else {
      //         // Handle error scenario if needed
      //         toast.error("Failed to upload image for form row:", i);
      //       }
      //     }
      //   }else if( formRow.image.image_url === ""){
      //     formRow.image.image_url = "";
      //     setFileMeta({
      //       size: 0,
      //       mime_type: "",
      //       width: 0,
      //       height: 0,
      //     });

      //    }else{

      //     formRow.description = "";
      //    }
      // }

      if (route !== "edit") {
        const formattedData = {
          ...data,
          sorting: parseInt(data.sorting),
          type: parseInt(data.type),
          thumbnail: {
            image_url: tempImageUrl,
            size: fileMeta.size,
            mime_type: fileMeta.mime_type,
            width: fileMeta.width,
            height: fileMeta.height,
          },
          items: updatedFormRows,
        };

        savePostForm(formattedData);
      } else {
        const formattedData = {
          ...data,
          sorting: parseInt(data.sorting),
          type: parseInt(data.type),
          thumbnail:
            fileMeta.size === 0
              ? post?.thumbnail
              : {
                image_url: tempImageUrl,
                size: fileMeta.size,
                mime_type: fileMeta.mime_type,
                width: fileMeta.width,
                height: fileMeta.height,
              },
          items: updatedFormRows,
        };

        savePostForm(formattedData, postId);
      }
    } catch (error) {
      toast.error("Error during form submission:", error);
      // Handle error state if needed
    } finally {
      setIsLoading(false);
    }
  };

  const savePostForm = (formattedData, postId = "") => {
    if (postId) {
      dispatch(editPost({ id: postId, data: formattedData }));
    } else {
      dispatch(addPost(formattedData));
    }
  };

  const handleImageUpload = async (file) => {
    const fileExtension = {
      extensions: [file?.name.split(".").pop()?.toLowerCase()],
    };

    try {
      const response = await Client(
        API_METHOD.POST,
        url.presigned_url,
        fileExtension
      );
      const presignedUrl = response.data.data.items[0].preSignedUrl;

      const imageTemp = response.data.data.items[0].fileName;

      const saveImage = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        redirect: "follow",
      });
      if (saveImage.status === 200) {
        return { imageTemp };
      } else {
        return { type: "error", message: "Failed to Upload Image" };
      }
    } catch (error) {
      return { type: "error", message: "Failed to Upload Image" };
    }
  };

  const handleViewImage = () => {
    setViewModel(true);
  };

  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };
  const handleFileChange = (file) => {
    if (file) {
      const img = new Image();
      img.onload = () => {
        setFileMeta({
          size: file.size / 1024, // size in KB
          mime_type: file.type,
          width: img.width,
          height: img.height,
        });
      };
      img.src = URL.createObjectURL(file);
    } else {
      setFileMeta({
        size: 0,
        mime_type: "",
        width: 0,
        height: 0,
      });
    }
  };
  const handleFormRowFileChange = (file, index) => {
    if (file) {
      const img = new Image();
      img.onload = () => {
        setFormRowsMeta((prevMeta) => ({
          ...prevMeta,
          [index]: {
            size: file.size / 1024, // size in KB
            mime_type: file.type,
            width: img.width,
            height: img.height,
          },
        }));
      };
      img.src = URL.createObjectURL(file);
    } else {
      setFormRowsMeta((prevMeta) => ({
        ...prevMeta,
        [index]: {
          size: 0,
          mime_type: "",
          width: 0,
          height: 0,
        },
      }));
    }
  };

  const getCustomStyles = (field) => ({

    multiValueRemove: (base, state) => {
      return state.data.isDisabled ? { ...base, display: "none" } : base;
    },

    control: (provided, state) => ({
      ...provided,
      borderColor: errors[field] ? "red" : provided.borderColor,
      boxShadow: "none",
      "&:hover": {
        borderColor: errors[field] ? "red" : provided.borderColor,
        boxShadow: "none",
      },
    }),
  });

  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          {viewModel && (
            <ViewModal
              show={viewModel}
              onCloseClick={() => setViewModel(false)}
              data={post?.thumbnail?.image_url}
            />
          )}

          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Post"
                  : route === "view"
                    ? "View Post"
                    : "Create Post"}
              </CardTitle>
              <FormProvider {...{ control, handleSubmit, setValue, errors }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <CustomLabel htmlFor="Sorting" isRequired>
                          Sorting
                        </CustomLabel>
                        <Controller
                          name="sorting"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <div>
                              <Input
                                {...field}
                                placeholder="Sorting"
                                invalid={!!errors.sorting}
                                maxLength={PHONE_NUMBER_MAXLENGTH}
                                onChange={(e) =>
                                  handleInputChange(e, "sorting")
                                }
                              />
                              {errors.sorting && (
                                <FormFeedback>
                                  {errors.sorting.message}
                                </FormFeedback>
                              )}
                            </div>
                          )}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <CustomLabel htmlFor="Post Title" isRequired>
                          Post Title
                        </CustomLabel>
                        <Controller
                          name="title"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <div>
                              <Input
                                {...field}
                                placeholder="Enter Post Title"
                                invalid={!!errors.title}
                                // value={country?.name??""}
                                maxLength={NAME_MAXLENGTH}
                              />
                              {errors.title && (
                                <FormFeedback>
                                  {errors.title.message}
                                </FormFeedback>
                              )}
                            </div>
                          )}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <CustomLabel htmlFor="Posts Type" isRequired>
                          Posts Type
                        </CustomLabel>
                        <Controller
                          name="type"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <>
                              <Select
                                {...field}
                                options={options.postOptions}
                                placeholder="Select Post Type"
                                onChange={(selectedOption) => {
                                  const selectedValue = selectedOption ? selectedOption.value : "";
                                  field.onChange(selectedValue);

                                  // Dynamically set an error for 'original_author_ids' if type is 2
                                  if (selectedValue === 2) {
                                    // setError("original_author_ids", {
                                    //   type: "manual",
                                    //   message: "Author is required",
                                    // });
                                  } else {
                                    clearErrors("original_author_ids");
                                  }
                                }}
                                value={options.postOptions.find(
                                  (option) => option.value === field.value
                                )}
                                styles={getCustomStyles("type")}
                                isClearable
                              />
                              {errors.type && (
                                <FormFeedback className="d-block">
                                  {errors.type.message}
                                </FormFeedback>
                              )}
                            </>
                          )}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      {/* <FormGroup>
                        <CustomLabel htmlFor="Author Name" isRequired>
                        Author Name
                        </CustomLabel>
                        <Controller
                          name="original_author_ids"
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <>
                              <Select
                                {...field}
                                options={options.authOptions}
                                placeholder="Select Author Name"
                                isMulti
                                onChange={(selectedOptions) =>
                                  field.onChange(
                                    selectedOptions.map(
                                      (option) => option.value
                                    )
                                  )
                                }
                                value={options.authOptions.filter((option) =>
                                  field.value.includes(option.value)
                                )}
                                styles={getCustomStyles("original_author_ids")}
                                isClearable
                              />
                              {errors.original_author_ids && (
                                <FormFeedback className="d-block">
                                  {errors.original_author_ids.message}
                                </FormFeedback>
                              )}
                            </>
                          )}
                        />
                      </FormGroup> */}

                      {/* <FormGroup>
                        <CustomLabel htmlFor="Author Name" isRequired>
                          Author Name
                        </CustomLabel>
                        <Controller
                          name="original_author_ids"
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => {
                            // Filter valid authors that are not deleted
                            const validAuthors = authors.filter(
                              (a) => a.deleted_at === null
                            );
                            const validAuthorIds = validAuthors.map(
                              (a) => a._id
                            );

                            // Filter the selected value to only include valid authors
                            const filteredValue = field.value.filter(
                              (selectedId) =>
                                validAuthorIds.includes(selectedId)
                            );

                            const handleChange = (selectedOptions) => {
                              const newValue = (selectedOptions || [])
                                .map((option) => option.value)
                                .filter((value) =>
                                  validAuthorIds.includes(value)
                                );
                              field.onChange(
                                newValue.length > 0 ? newValue : []
                              );
                            };

                            // Group options based on the route
                            const groupedOptions =
                              route === "add"
                                ? [
                                    {
                                      label: "Active",
                                      options: options.authOptions.filter(
                                        (option) =>
                                          !option.isDisabled &&
                                          !option.deleted_at
                                      ),
                                    },
                                  ]
                                : [
                                    {
                                      label: "Active",
                                      options: options.authOptions.filter(
                                        (option) =>
                                          !option.isDisabled &&
                                          !option.deleted_at
                                      ),
                                    },
                                    {
                                      label: "Inactive",
                                      options: options.authOptions.filter(
                                        (option) => option.isDisabled
                                      ),
                                    },
                                  ];

                            return (
                              <>
                                <Select
                                  {...field}
                                  options={groupedOptions}
                                  placeholder="Select Author Name"
                                  isMulti
                                  onChange={handleChange}
                                  value={options.authOptions.filter(
                                    (option) =>
                                      !option.isDisabled &&
                                      filteredValue.includes(option.value)
                                  )}
                                  styles={getCustomStyles(
                                    "original_author_ids"
                                  )}
                                  isClearable
                                  menuPlacement="auto"
                                  minMenuHeight={150}
                                  maxMenuHeight={150}
                                  isMenuScrollable
                                />
                                {errors.original_author_ids && (
                                  <FormFeedback className="d-block">
                                    {errors.original_author_ids.message}
                                  </FormFeedback>
                                )}
                              </>
                            );
                          }}
                        />
                      </FormGroup> */}
                      <FormGroup>
                        <CustomLabel htmlFor="Author Name" >
                          Author Name
                        </CustomLabel>
                        <Controller
                          name="original_author_ids"
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => {
                            const groupedOptions =
                              route === "add"
                                ? [
                                  {
                                    label: "Active",
                                    options: options.authOptions.filter(
                                      (option) =>
                                        !option.isDisabled &&
                                        !option.deleted_at
                                    ),
                                  },
                                  {
                                    label: "Inactive",
                                    options: options.authOptions.filter(
                                      (option) => option.isDisabled
                                    ),
                                  },
                                ]
                                : [
                                  {
                                    label: "Active",
                                    options: options.authOptions.filter(
                                      (option) =>
                                        !option.isDisabled &&
                                        !option.deleted_at
                                    ),
                                  },
                                  {
                                    label: "Inactive",
                                    options: options.authOptions.filter(
                                      (option) => option.isDisabled
                                    ),
                                  },
                                ];

                            return (
                              <>
                                <Select
                                  {...field}
                                  options={groupedOptions} // Use groupedOptions here
                                  placeholder="Select Author Name"
                                  isMulti
                                  onChange={(selectedOptions) =>
                                    field.onChange(
                                      selectedOptions
                                        ? selectedOptions.map(
                                          (option) => option.value
                                        )
                                        : []
                                    )
                                  }
                                  value={options.authOptions.filter((option) =>
                                    field.value.includes(option.value)
                                  )}
                                  styles={getCustomStyles(
                                    "original_author_ids"
                                  )}
                                  isClearable={
                                    groupedOptions[0].options.length > 0
                                  }
                                  menuPlacement="auto"
                                  menuShouldScrollIntoView={true}
                                  minMenuHeight={150}
                                  maxMenuHeight={150}
                                  isMenuScrollable={true}
                                />
                                {errors.original_author_ids && (
                                  <FormFeedback className="d-block">
                                    {errors.original_author_ids.message}
                                  </FormFeedback>
                                )}
                              </>
                            );
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <CustomLabel htmlFor="image" isRequired>
                          Thumbnail
                        </CustomLabel>
                        {route !== "add" ? (
                          <button
                            type="button"
                            className="view-button"
                            onClick={() => {
                              handleViewImage();
                            }}
                          >
                            View
                          </button>
                        ) : null}

                        <Controller
                          name="image"
                          control={control}
                          defaultValue={null}
                          render={({ field }) => (
                            <div>
                              <Input
                                type="file"
                                accept="image/jpeg,image/png"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  field.onChange(file);
                                  handleFileChange(file);
                                }}
                                invalid={!!errors.image}
                              />
                              {errors.image && (
                                <FormFeedback>
                                  {errors.image.message}
                                </FormFeedback>
                              )}
                            </div>
                          )}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Col md={12}>
                    <FormRepeater data={post?.contents || []} route={route} />
                  </Col>
                  <Row>
                    <Col md={6}></Col>
                    <Col md={6}>
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          type="button"
                          color="secondary"
                          onClick={() => {
                            navigate(`/posts?current_page=${pageQuery}`);
                            dispatch(resetError());
                          }}
                        >
                          {route === "view" ? "Back" : "Cancel"}
                        </Button>
                        {route !== "view" ? (
                          <Button
                            type="submit"
                            color={isLoading ? "success" : "primary"}
                          >
                            {isLoading ? (
                              <>Loading...</>
                            ) : (
                              <>
                                {route === "edit" ? "Edit Post" : "Create post"}
                              </>
                            )}
                          </Button>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </form>
              </FormProvider>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddPost;
